import { useState } from "react";
import api from "../../utils/api";
import { stopPushForThisDevice } from "../../utils/pushNotifications";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// This page is ONLY shown for first-time forced password change (mustChangePassword === true).
// Regular users access Change Password via the user dropdown in the header (ChangePasswordModal).

const EyeToggle = ({ show, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
  >
    {show ? (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      </svg>
    ) : (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    )}
  </button>
);

const PasswordField = ({ label, value, onChange, show, onToggle, placeholder }) => (
  <div className="mb-4">
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
      {label}
    </label>
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </span>
      <input
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-11 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
      />
      <EyeToggle show={show} onToggle={onToggle} />
    </div>
  </div>
);

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.warning("Passwords do not match");
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.post("/auth/change-password", {
        currentPassword,
        newPassword,
      });
      toast.success(response.data.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordChanged(true);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error updating password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    // Finish before leaving the page, or the browser may cancel it
    await stopPushForThisDevice(); // no more order alerts on this device
    ["token", "username", "role", "userId", "mustChangePassword", "rememberMe", "savedUsername"]
      .forEach((k) => localStorage.removeItem(k));
    window.location.href = "/";
  };

  // Success state — prompt logout
  if (passwordChanged) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">        <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-black p-8 text-center">
            <div className="w-16 h-16 rounded-full border-2 border-white flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-white text-xl font-bold">Password Updated!</h2>
          </div>
          <div className="p-8 text-center">
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Your password has been set. Please sign in again with your new password to continue.
            </p>
            <button
              onClick={handleLogout}
              className="w-full bg-black text-white py-3.5 rounded-xl font-semibold text-sm tracking-wide hover:bg-gray-900 active:scale-[0.98] transition-all duration-200"
            >
              Logout & Sign In Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-black px-8 py-7">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h1 className="text-white text-lg font-bold">Set New Password</h1>
          </div>
          <p className="text-gray-400 text-xs ml-11">
            This is your first login. Set a new password to continue.
          </p>
        </div>

        {/* Form */}
        <div className="px-8 py-7">
          <div className="mb-5 flex items-start gap-2 p-3 border border-gray-200 rounded-xl bg-gray-50">
            <svg className="w-4 h-4 text-black shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" />
            </svg>
            <p className="text-xs text-gray-600 leading-relaxed">
              You must set a new password before you can access the dashboard.
            </p>
          </div>

          <PasswordField
            label="Current (Temporary) Password"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            onToggle={() => setShowCurrent(!showCurrent)}
            placeholder="Enter your temporary password"
          />
          <PasswordField
            label="New Password"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggle={() => setShowNew(!showNew)}
            placeholder="Choose a new password"
          />
          <PasswordField
            label="Confirm New Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showConfirm}
            onToggle={() => setShowConfirm(!showConfirm)}
            placeholder="Re-enter new password"
          />

          <button
            onClick={handleChangePassword}
            disabled={isLoading}
            className="w-full mt-2 bg-black text-white py-3.5 rounded-xl font-semibold text-sm tracking-wide hover:bg-gray-900 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Updating...
              </>
            ) : (
              "Set Password & Continue"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
